import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_CLIENTS_SHORT_ENTITIES,
} from '@/lib/constants/pagination';
import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { CreatedEntity } from '@/modules/common/types/created-entity';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { axiosInstance } from '@/utils/axios';

import { mapClientFromClientResponse } from '../mappers/client-response.mapper';
import { mapShortClientsToDropdownItems } from '../mappers/client-short.mapper';
import { mapClientSummaries } from '../mappers/client-summary.mapper';
import { Client } from '../types';
import { ShortClientResponse } from '../types/dto/client-short.dto';
import { CreateClientRequest } from '../types/dto/request/create-client.dto';
import { UpdateClientRequest } from '../types/dto/request/update-client.dto';
import { ClientResponse } from '../types/dto/response/client-response.dto';
import { ClientSummaryResponse } from '../types/dto/response/client-summary-response';
import {
  ArchiveClientRequest,
  DeleteClientsBulkRequest,
  UnarchiveClientRequest,
} from '../types/request';
import {
  FindClientSummariesFilterRequest,
  FindClientSummariesRequest,
} from '../types/request/find-client-summaries.request';
import {
  FindShortClientsFilterRequest,
  FindShortClientsRequest,
} from '../types/request/find-short-clients.request';

/**
 * Retrieves client summaries with pagination and filtering support
 * Used in main clients table/list view
 * POST /api/clients/summaries/search
 *
 * @param params - Filter and pagination parameters
 * @returns Paginated list of client summaries
 */
export const getClientSummaries = async (params?: {
  filter?: FindClientSummariesFilterRequest;
  pagination?: { cursor?: number | null; pageSize?: number };
}): Promise<PaginatedResponse<Client>> => {
  const endpoint = '/api/clients/summaries/search';

  const requestBody: FindClientSummariesRequest = {
    filter: params?.filter,
    pagination: {
      pageSize: params?.pagination?.pageSize ?? DEFAULT_PAGE_SIZE,
      cursor: params?.pagination?.cursor,
    },
  };

  const apiData = await withApiErrorHandler(endpoint, ApiErrorCode.CLIENTS_LOAD_FAILED, () =>
    axiosInstance.post<{
      data: PaginatedResponse<ClientSummaryResponse>;
      error?: { status: number; message: string; code: string };
    }>(endpoint, requestBody)
  );

  return new PaginatedResponse({
    items: mapClientSummaries(apiData.items),
    pagination: {
      total: apiData.pagination.total,
      cursor: apiData.pagination.cursor,
    },
  });
};

/**
 * Retrieves short client list with pagination support
 * Used in dropdowns and selectors for client selection
 * POST /api/clients/short/search
 *
 * @param params - Filter and pagination parameters
 * @returns Paginated list of clients as DropdownSearchItem
 */
export const getShortClients = async (params?: {
  filter?: FindShortClientsFilterRequest;
  pagination?: { cursor?: number | null; pageSize?: number };
}): Promise<PaginatedResponse<DropdownSearchItem>> => {
  const endpoint = '/api/clients/short/search';

  const requestBody: FindShortClientsRequest = {
    filter: params?.filter,
    pagination: {
      pageSize: params?.pagination?.pageSize ?? DEFAULT_PAGE_SIZE_CLIENTS_SHORT_ENTITIES,
      cursor: params?.pagination?.cursor,
    },
  };

  const response = await axiosInstance.post<{
    data: {
      items: ShortClientResponse[];
      pagination: {
        total: number;
        cursor: number | null;
      };
    };
    error?: { status: number; message: string; code: string };
  }>(endpoint, requestBody);

  if (response.data.error) {
    throw new Error(response.data.error.message);
  }

  const apiData = response.data.data;

  return new PaginatedResponse({
    items: mapShortClientsToDropdownItems(apiData.items),
    pagination: {
      total: apiData.pagination.total,
      cursor: apiData.pagination.cursor,
    },
  });
};

/**
 * Retrieves detailed client information by ID.
 */
export const getClientDetails = async (clientId?: string): Promise<Client> => {
  if (!clientId) {
    throw new Error('Client ID is required');
  }

  const endpoint = '/api/clients/one';

  const res = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.get<{ data: ClientResponse }>(endpoint, {
      params: {
        clientId: +clientId,
      },
    })
  );

  return mapClientFromClientResponse(res);
};

/**
 * Creates a new client with the provided configuration
 * POST /api/clients/one
 * Returns only the client ID, not the full client object
 */
export const createClient = async (data: CreateClientRequest): Promise<CreatedEntity> => {
  const endpoint = '/api/clients/one';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.post<{
      data: CreatedEntity;
      error?: { status: number; message: string; code: string };
    }>(endpoint, data)
  );

  return response;
};

/**
 * Updates existing client with new data
 * PATCH /api/clients/one
 */
export const updateClient = async (data: UpdateClientRequest): Promise<void> => {
  const endpoint = '/api/clients/one';

  await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.patch<{
      data: ClientResponse;
      error?: { status: number; message: string; code: string };
    }>(endpoint, data)
  );
};

/**
 * Archives a client
 * POST /api/clients/archive
 */
export const archiveClient = async (clientId: string): Promise<void> => {
  const endpoint = '/api/clients/archive';

  const requestBody: ArchiveClientRequest = {
    clientId: Number(clientId),
  };

  await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.post<{
      data: Record<string, never>;
      error?: { status: number; message: string; code: string };
    }>(endpoint, requestBody)
  );
};

/**
 * Unarchives a client
 * POST /api/clients/unarchive
 */
export const unarchiveClient = async (clientId: string): Promise<void> => {
  const endpoint = '/api/clients/unarchive';

  const requestBody: UnarchiveClientRequest = {
    clientId: Number(clientId),
  };

  await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.post<{
      data: Record<string, never>;
      error?: { status: number; message: string; code: string };
    }>(endpoint, requestBody)
  );
};

/**
 * Deletes multiple clients permanently
 * DELETE /api/clients/bulk
 */
export const deleteClients = async (clientIds: string[]): Promise<void> => {
  const endpoint = '/api/clients/bulk';

  const requestBody: DeleteClientsBulkRequest = {
    ids: clientIds.map(id => Number(id)),
  };

  await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.delete<{
      data: Record<string, never>;
      error?: { status: number; message: string; code: string };
    }>(endpoint, { data: requestBody })
  );
};

/**
 * Uploads client logo image file using presigned URL approach.
 * Steps:
 * 1. Get presigned URL from backend
 * 2. Upload file directly to S3 using presigned URL
 * 3. Return the final accessible URL
 */
export const uploadClientLogo = async ({
  clientId,
  file,
}: {
  clientId: string;
  file: File;
}): Promise<string> => {
  const endpoint = '/api/clients/update-image-url';

  // Step 1: Get presigned URL from backend
  const response = await withApiErrorHandler(endpoint, ApiErrorCode.CLIENT_LOGO_UPLOAD_FAILED, () =>
    axiosInstance.get<{
      data: { presignedUrl: string };
      error?: { status: number; message: string; code: string };
    }>(endpoint, {
      params: {
        clientId: parseInt(clientId, 10),
        fileName: file.name,
        contentLength: file.size,
      },
    })
  );

  const { presignedUrl } = response;

  // Step 2: Upload file to S3 using presigned URL
  await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  // Step 3: Extract the base URL (without query parameters) as the accessible logo URL
  return presignedUrl.split('?')[0];
};
